// Coarse relative-time labels for the character launcher's "opened …" line.
//
// Deliberately coarse: the launcher only needs "is this the one I was just in, or
// something I have not touched in a week". Minute-level precision past the first
// hour would be noise, and an exact timestamp would be worse — it makes the reader
// do the arithmetic.
//
// Pure and injectable: `now` is a parameter, never Date.now() read internally, so
// the output is testable without freezing the clock.

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * @param {number} then  epoch ms
 * @param {number} now   epoch ms
 * @returns {string|null} e.g. "just now", "2 min ago", "3 hr ago", "yesterday",
 *   "3 days ago", "2 weeks ago". `null` when `then` is unusable — the caller then
 *   renders nothing rather than a fabricated time.
 */
export function formatRelativeTime(then, now) {
  if (!Number.isFinite(then) || !Number.isFinite(now)) return null

  const delta = now - then
  // A future timestamp means a clock change or a corrupt record. "just now" is the
  // honest reading of "not in the past"; inventing "in 3 days" would be worse.
  if (delta < 0) return 'just now'

  if (delta < MINUTE) return 'just now'
  if (delta < HOUR) {
    const m = Math.floor(delta / MINUTE)
    return `${m} min ago`
  }
  if (delta < DAY) {
    const h = Math.floor(delta / HOUR)
    return `${h} hr ago`
  }

  const days = Math.floor(delta / DAY)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`

  const weeks = Math.floor(days / 7)
  if (weeks === 1) return 'last week'
  if (weeks < 5) return `${weeks} weeks ago`

  const months = Math.floor(days / 30)
  if (months <= 1) return 'last month'
  if (months < 12) return `${months} months ago`
  return 'over a year ago'
}
